import { buildAmountsSummary, round2 } from './build-amounts-summary';
import { toNumber } from './utils';

/**
 * XPEN-3609: taxes of an expense-report invoice (type `XPENSE`), recalculated on the
 * invoice screen.
 *
 * xpens-api `/utils/compute_taxes` (`compute_invoice_taxes`, `src/utils/utils.py`) taxes
 * each line's `Total`, which on these invoices includes the tip. Sent with
 * `type: "expense_invoice"`, it taxes the amount before the tip
 * (`amounts_summary.ItemSubTotal`), but it recalculates every line it is given that has
 * a known tax code, and rebuilds every line's `amounts_summary`. So the invoice screen
 * sends it only the line that changed, and adds the header up from the lines itself:
 * every other line keeps the taxes it has.
 */

// The four header fields `compute_invoice_taxes` sets, rounded as it rounds them: each
// tax half up (`_to_fixed_2`) on the sum of the lines, `Total` the plain sum
// (`change_total`), and the total with tax as Python `round`
// (`compute_invoice_grand_total`). Unlike it, every line's own taxes count, including
// a line it would skip for having no known tax code.
export function addUpInvoiceTotals(json) {
  const lines = json?.ListItem || [];
  const sum = (field) => lines.reduce((total, line) => total + toNumber(line?.[field]), 0);
  const total = sum('Total');
  const tax1 = roundHalfUp2(sum('Tax1'));
  const tax2 = roundHalfUp2(sum('Tax2'));
  return {
    ...json,
    Total: total,
    Tax1: tax1,
    Tax2: tax2,
    TotalWithTax: round2(total + tax1 + tax2),
  };
}

/**
 * The `/utils/compute_taxes` request for the one line whose tax code or amounts changed.
 *
 * The rest of the invoice is kept: the server reads its default tax code, tax deduction,
 * model and supplier. A line without an amount before the tip (no `amounts_summary`, or
 * one without `ItemSubTotal`) gets one, worked out as the server's `_compute_amounts_summary`
 * works it out (`buildAmountsSummary`); without it, the server would tax 0, or fail on a
 * null `amounts_summary`.
 *
 * @returns {{body: object, sent: object} | null} The request, and what to give
 *   `mergeLineTaxes` with its answer; null when no line has that `_view_id`.
 * @throws {RangeError} When that amount has to be built at a tax deduction of 100% or more.
 */
export function buildLineTaxRequest(json, viewId) {
  const line = findLine(json, viewId);
  if (!line) return null;
  const copy = JSON.parse(JSON.stringify(line));
  if (copy.amounts_summary?.ItemSubTotal == null) {
    const { ItemSubTotal } = buildAmountsSummary(copy, json.TaxDeduction);
    copy.amounts_summary = { ...copy.amounts_summary, ItemSubTotal };
  }
  return {
    body: { ...json, type: 'expense_invoice', ListItem: [copy] },
    sent: { viewId, lineCount: json.ListItem.length, inputs: taxInputs(json, line) },
  };
}

/**
 * The server's answer for one line, checked against the invoice as it is now.
 *
 * It is written only onto the line it was asked for: the same `_view_id` in a list of the
 * same length, still with every input the answer was computed from. Only that line's
 * taxes and `amounts_summary` are taken, never the answer's header.
 *
 * @returns {{listItems: object[]} | {reason: string}} The new lines; or why not:
 *   `line_changed` (the line is there but has changed since: ask again),
 *   `lines_changed` (lines were added or removed, so it cannot be told apart), or
 *   `no_answer` (the answer holds no such line).
 */
export function mergeLineTaxes(json, sent, answer) {
  const lines = json?.ListItem || [];
  const line = findLine(json, sent.viewId);
  if (!line || lines.length !== sent.lineCount) return { reason: 'lines_changed' };
  if (taxInputs(json, line) !== sent.inputs) return { reason: 'line_changed' };
  const answered = findLine(answer, sent.viewId);
  if (!answered) return { reason: 'no_answer' };
  return {
    listItems: lines.map((item) => (item === line ? {
      ...item,
      Tax1: answered.Tax1,
      Tax2: answered.Tax2,
      Tax: answered.Tax,
      amounts_summary: answered.amounts_summary,
    } : item)),
  };
}

/**
 * The invoice without its separate BC tax line (`Type: "BCGSTPSTIN"`) once no billable
 * line with the BCGSTPSTIN tax code is left: xpens-api `compute_invoice_taxes` removes
 * it then too (XPEN-3611). The lines are numbered again, as a deletion numbers them.
 *
 * While such a line is left, the BC tax line is kept as it is: its amount is worked out
 * by the server from the tax rates, which the invoice screen leaves to it (XPEN-2798).
 */
export function withoutOrphanBcTaxLine(json) {
  const lines = json?.ListItem || [];
  const isBcTaxLine = (line) => line?.Type === 'BCGSTPSTIN';
  if (!lines.some(isBcTaxLine)) return json;
  const billableBcLineLeft = lines.some((line) => !isBcTaxLine(line)
    && Boolean(line?.IsBillable)
    && (line?.TaxCode || json.TaxCode) === 'BCGSTPSTIN');
  if (billableBcLineLeft) return json;
  return {
    ...json,
    ListItem: lines.filter((line) => !isBcTaxLine(line)).map((line, index) => ({ ...line, _view_id: index })),
  };
}

function findLine(json, viewId) {
  return (json?.ListItem || []).find((line) => line?._view_id === viewId);
}

// Everything the server's answer for this line is computed from, apart from the client's
// tax profile: the line's tax code, amounts, taxes and type, and the invoice's default
// tax code, tax deduction, model and supplier numbers.
function taxInputs(json, line) {
  const amount = (value) => (value == null || value === '' ? null : toNumber(value));
  return JSON.stringify([
    line.TaxCode || null,
    line.Type || null,
    Boolean(line.IsBillable),
    ...['Total', 'SubTotal', 'TipAllocated', 'TaxDeducted', 'Tax1', 'Tax2', 'Tax'].map((field) => amount(line[field])),
    amount(line.amounts_summary?.ItemSubTotal),
    json.TaxCode || null,
    amount(json.TaxDeduction),
    json.Model || null,
    (json.ListSupplier || []).map((supplier) => supplier?.Number ?? null),
  ]);
}

// xpens-api `_round_half_up_2_decimals`, on the same double.
function roundHalfUp2(value) {
  const sign = value < 0 ? -1 : 1;
  return (Math.floor(Math.abs(value) * 100 + 0.5) / 100) * sign;
}
